// Shell Sidebar — wraps DS `Sidebar` + `NavItem` for the production shell.
//
// US-039 (sidebar shape), US-040 (active highlight), US-041 (inbox unread
// badge), US-042 (accounts alert dot), US-043 (user-card → Settings),
// US-044 (user-card dropdown), US-055 (brand lockup follows theme variables).
//
// The component is purely presentational: nav targets, active route, badge
// state, and the user-card avatar are all controlled from `<AppShell>`. We
// keep the Wave 4.2 logout wiring intact by routing it through
// `<UserCardDropdown>` which calls `useSignOut()` + `auth.logout.successToast`.

import { Link, useRouterState } from '@tanstack/react-router';
import {
  Briefcase,
  Building2,
  Bell,
  ChevronUp,
  Inbox,
  Megaphone,
  Settings as SettingsIcon,
  Users,
} from 'lucide-react';
import { forwardRef, useState, type ReactNode } from 'react';

import { useT } from '@barel/i18n';
import { BrandLockup, NavItem, Sidebar as DsSidebar } from '@barel/ui';
import type { AuthUser } from '@barel/api-client/react';

type NavItemBadge = 'dot' | 'count' | 'alert';

import { TitleTooltip } from './Tooltip';
import { UserCardDropdown } from './UserCardDropdown';

type NavId = 'inbox' | 'accounts' | 'businesses' | 'campaigns' | 'leads' | 'relances';

interface NavDescriptor {
  id: NavId;
  to: string;
  /** Active when the current pathname matches this prefix, AFTER the explicit `to` match. */
  activePrefixes: readonly string[];
  icon: ReactNode;
  labelKey: string;
}

const NAV_ITEMS: readonly NavDescriptor[] = [
  {
    id: 'inbox',
    to: '/inbox',
    activePrefixes: ['/inbox'],
    icon: <Inbox size={16} aria-hidden="true" />,
    labelKey: 'nav.inbox',
  },
  {
    id: 'accounts',
    to: '/accounts',
    activePrefixes: ['/accounts'],
    icon: <Users size={16} aria-hidden="true" />,
    labelKey: 'nav.accounts',
  },
  {
    id: 'businesses',
    to: '/businesses',
    activePrefixes: ['/businesses'],
    icon: <Building2 size={16} aria-hidden="true" />,
    labelKey: 'nav.businesses',
  },
  {
    id: 'campaigns',
    to: '/campaigns',
    activePrefixes: ['/campaigns'],
    icon: <Megaphone size={16} aria-hidden="true" />,
    labelKey: 'nav.campaigns',
  },
  {
    id: 'leads',
    to: '/leads',
    activePrefixes: ['/leads'],
    icon: <Briefcase size={16} aria-hidden="true" />,
    labelKey: 'nav.leads',
  },
  {
    id: 'relances',
    // D25 (Q4): top-level relances slot. `/relances` redirects to the only
    // current sub-page (`/pending`); we link the bare path so the URL bar
    // stays clean on landing.
    to: '/relances',
    activePrefixes: ['/relances'],
    icon: <Bell size={16} aria-hidden="true" />,
    labelKey: 'nav.relances',
  },
];

export interface SidebarProps {
  /** Currently authenticated user (from `useAuthGuard()`). */
  user: AuthUser | undefined;
  /** Inbox-unread indicator (US-041). When `true`, the inbox nav row gets a dot. */
  hasUnread?: boolean;
  /** Accounts-alert indicator (US-042). When `true`, the accounts row gets a red dot. */
  accountAlert?: boolean;
}

function deriveInitials(user: AuthUser | undefined, fallback: string): string {
  const source = user?.name?.trim() ?? user?.email?.trim() ?? '';
  if (!source) return fallback;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  if (parts.length === 1) {
    const head = parts[0] ?? '';
    return head.slice(0, 2).toUpperCase();
  }
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return ((first.charAt(0) ?? '') + (last.charAt(0) ?? '')).toUpperCase();
}

/**
 * Shell Sidebar — composes DS `Sidebar` slots (top / nav / bottom) with the
 * production nav item set, the brand block, and the user-card dropdown.
 */
export const Sidebar = forwardRef<HTMLElement, SidebarProps>(function Sidebar(
  { user, hasUnread = false, accountAlert = false },
  ref,
) {
  const t = useT('shell');
  const tCommon = useT('common');
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (descriptor: NavDescriptor): boolean => {
    if (descriptor.id === 'inbox') {
      return pathname === '/' || pathname.startsWith('/inbox');
    }
    return descriptor.activePrefixes.some((prefix) => pathname.startsWith(prefix));
  };

  const top = <BrandLockup size={28} aria-label={t('brand.name')} />;

  const nav = (
    <ul
      role="list"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-1)',
        margin: 0,
        padding: 0,
        listStyle: 'none',
      }}
    >
      {NAV_ITEMS.map((item) => {
        const active = isActive(item);
        let badge: NavItemBadge | null = null;
        let alertLabel: string | undefined;
        if (item.id === 'inbox' && hasUnread && !active) badge = 'dot';
        if (item.id === 'accounts' && accountAlert && !active) {
          badge = 'alert';
          alertLabel = t('nav.accountsAlertLabel');
        }
        const node = (
          <NavItem
            asChild
            icon={item.icon}
            active={active}
            badge={badge}
            alertLabel={alertLabel}
            data-testid={`nav-${item.id}`}
          >
            <Link to={item.to}>{t(item.labelKey)}</Link>
          </NavItem>
        );
        return (
          <li key={item.id}>
            {item.id === 'accounts' && accountAlert && !active ? (
              <TitleTooltip label={t('nav.accountsAlertLabel')} side="right">
                <span style={{ display: 'block' }}>{node}</span>
              </TitleTooltip>
            ) : (
              node
            )}
          </li>
        );
      })}
    </ul>
  );

  const initials = deriveInitials(user, t('userCard.fallbackInitials'));
  const displayName = user?.name?.trim() || user?.email?.trim() || t('userCard.fallbackName');
  const displayEmail = user?.email ?? tCommon('states.loading');

  const userCardButton = (
    <button
      type="button"
      aria-label={t('userCard.openMenuLabel')}
      data-testid="user-card-trigger"
      className="ls-focus-ring"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        width: '100%',
        padding: 'var(--space-2)',
        borderRadius: 'var(--radius-md)',
        background: menuOpen ? 'var(--bg-hover)' : 'transparent',
        border: '1px solid transparent',
        color: 'var(--text-primary)',
        cursor: 'pointer',
        textAlign: 'left',
        font: 'inherit',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border-strong)',
          color: 'var(--text-primary)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 'var(--text-2xs)',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {initials}
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span
          style={{
            display: 'block',
            fontSize: 'var(--text-2xs)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayName}
        </span>
        <span
          style={{
            display: 'block',
            fontSize: 'var(--text-2xs)',
            color: 'var(--text-tertiary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayEmail}
        </span>
      </span>
      <ChevronUp
        size={14}
        aria-hidden="true"
        style={{
          color: 'var(--text-tertiary)',
          transform: menuOpen ? 'rotate(0deg)' : 'rotate(180deg)',
          transition: 'transform var(--motion-fast) ease-out',
          flexShrink: 0,
        }}
      />
    </button>
  );

  const bottom = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <NavItem
        asChild
        icon={<SettingsIcon size={16} aria-hidden="true" />}
        active={pathname.startsWith('/settings')}
        data-testid="nav-settings"
      >
        <Link to="/settings/$section" params={{ section: 'profile' }}>
          {t('nav.settings')}
        </Link>
      </NavItem>
      <UserCardDropdown open={menuOpen} onOpenChange={setMenuOpen}>
        {userCardButton}
      </UserCardDropdown>
    </div>
  );

  return (
    <DsSidebar
      ref={ref}
      navLabel={t('nav.label')}
      top={top}
      nav={nav}
      bottom={bottom}
      data-testid="shell-sidebar"
    />
  );
});

Sidebar.displayName = 'ShellSidebar';
